import { MouseEvent, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./ColumnResizeHandle.scss";

/**
 * Smallest width a user may drag ANY column down to (px). A hard global floor
 * used only when the column carries no type-aware min-width (see `minWidth`
 * prop) — otherwise the per-datatype floor wins.
 */
const MIN_COLUMN_WIDTH = 48;

type ColumnResizeHandleProps = {
    columnKey: string;
    /**
     * Report a new width for the column. Called repeatedly while dragging with
     * `commit=false` (live preview) and once on mouse-up with `commit=true`
     * (persist under the table's settings key).
     */
    onResize: (columnKey: string, width: number, commit: boolean) => void;
    /** Clear the user override, restoring the type-aware default width caps. */
    onResetWidth?: (columnKey: string) => void;
    /**
     * Per-datatype minimum width (px) the drag cannot cross for THIS column,
     * derived from the column's type-aware cellStyle floor. Without it a user
     * could drag any column down to the global `MIN_COLUMN_WIDTH`, shrinking the
     * cell below the intrinsic size of the control it renders (e.g. a true/false
     * toggle) so the control spills out past the cell border into its neighbours
     * (Margot, operator-task-fe-resizable-column-narrow-width-behavior). Clamping
     * the drag to the column's own floor keeps every cell's content contained at
     * the narrowest draggable width. Falls back to `MIN_COLUMN_WIDTH`.
     */
    minWidth?: number;
};

/**
 * A hand-rolled column-resize grip rendered at the right edge of a RecordTable
 * header cell. On mouse-down it measures the header cell's current pixel width
 * and, while dragging, reports the new width live so the column re-renders; on
 * mouse-up it reports the final width once with `commit=true` so the parent
 * RecordTable persists it. A double-click clears the override, restoring the
 * type-aware default caps.
 *
 * Why hand-rolled: mui-datatables 4.3.0 exposes no column-resize persistence
 * hook (its built-in `resizableColumns` is percentage-based with no width
 * callback), so the width is measured from the live `<th>` via
 * getBoundingClientRect — robust regardless of which ancestor is the positioned
 * offset parent.
 *
 * Why a portal into the `<th>`: mui-datatables injects a column's
 * `customHeadLabelRender` output INSIDE its clickable sort `<button>` for every
 * SORTABLE column (non-sortable columns render the label directly under the
 * `<th>`). A grip nested inside that native `<button>` does not receive real
 * mouse drags — the button intercepts the press — so sortable columns could not
 * be resized (only non-sortable ones could). Rendering the grip through a portal
 * as a DIRECT child of the `<th>` (a sibling of, not a descendant of, the sort
 * button) makes every column behave identically to the working non-sortable
 * case. The grip stays absolutely positioned against the `<th>` (its offset
 * parent), so it still sits on the column's right border.
 */
const ColumnResizeHandle = ({
    columnKey,
    onResize,
    onResetWidth,
    minWidth,
}: ColumnResizeHandleProps) => {
    // The narrowest width this column may be dragged to: its own type-aware
    // floor when present, else the global hard minimum.
    const columnMinWidth = typeof minWidth === "number" ? Math.max(minWidth, 0) : MIN_COLUMN_WIDTH;
    // A zero-size anchor rendered in the header-label subtree; we use it only to
    // locate the enclosing <th>, then portal the actual grip there.
    const anchorRef = useRef<HTMLSpanElement>(null);
    const [headerCell, setHeaderCell] = useState<HTMLTableCellElement | null>(null);

    useLayoutEffect(() => {
        // Resolve the enclosing <th> once on mount. A column reorder/remount
        // mounts a fresh handle, so the anchor's <th> ancestor is stable for this
        // component's lifetime; no dependency re-run is needed.
        const cell = anchorRef.current?.closest("th") as HTMLTableCellElement | null;
        setHeaderCell(cell ?? null);
    }, []);

    const handleMouseDown = (event: MouseEvent<HTMLSpanElement>) => {
        // Don't let the drag start a header sort or select header text.
        event.preventDefault();
        event.stopPropagation();

        const cell = (event.currentTarget as HTMLElement).closest("th");
        const startX = event.clientX;
        const startWidth = cell ? cell.getBoundingClientRect().width : 0;

        const computeWidth = (clientX: number) =>
            Math.max(columnMinWidth, Math.round(startWidth + (clientX - startX)));

        const handleMouseMove = (moveEvent: globalThis.MouseEvent) => {
            onResize(columnKey, computeWidth(moveEvent.clientX), false);
        };

        const handleMouseUp = (upEvent: globalThis.MouseEvent) => {
            document.removeEventListener("mousemove", handleMouseMove);
            document.removeEventListener("mouseup", handleMouseUp);
            document.body.classList.remove("columnResizing");
            onResize(columnKey, computeWidth(upEvent.clientX), true);
        };

        document.body.classList.add("columnResizing");
        document.addEventListener("mousemove", handleMouseMove);
        document.addEventListener("mouseup", handleMouseUp);
    };

    const handleDoubleClick = (event: MouseEvent<HTMLSpanElement>) => {
        event.preventDefault();
        event.stopPropagation();
        onResetWidth?.(columnKey);
    };

    const grip = (
        <span
            className="columnResizeHandle"
            data-column-key={columnKey}
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize column"
            title="Drag to resize · double-click to reset"
            onMouseDown={handleMouseDown}
            onClick={(event) => event.stopPropagation()}
            onDoubleClick={handleDoubleClick}
        />
    );

    return (
        <>
            <span ref={anchorRef} className="columnResizeHandleAnchor" aria-hidden="true" />
            {headerCell ? createPortal(grip, headerCell) : null}
        </>
    );
};

export default ColumnResizeHandle;
export { MIN_COLUMN_WIDTH };
